import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiService } from '../services/apiService';
import { ClassNavigation } from '../components/layout';
import { UserCircleIcon, AcademicCapIcon, ChartBarIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { CategoryScore, BackgroundResponse, SurveyResponse } from '../types/survey-student';

interface Student {
  id: string;
  name: string;
  email: string;
  enrollmentDate: string;
  lastActive: string;
  engagementScore: number;
  feedbackCompleted: number;
  status: 'active' | 'inactive' | 'at-risk';
  auraProfile: {
    dominant: string;
    secondary: string;
    color: string;
    description: string;
  };
  learningProfile: {
    academicBackground: string[];
    learningGoals: string[];
    preferredLearningStyles: string[];
    participationComfort: string[];
    workloadPreferences: string[];
    assessmentStyles: string[];
  };
}

interface ClassInfo {
  id: string;
  name: string;
  code: string;
  term: string;
}

const StudentDetails = () => {
  const { classId, studentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [student, setStudent] = useState<Student | null>(null);
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [surveyData, setSurveyData] = useState<{
    scores: CategoryScore[];
    backgroundData: BackgroundResponse;
    responses: SurveyResponse[];
    hasResults: boolean;
  } | null>(null);
  const [backgroundData, setBackgroundData] = useState<BackgroundResponse | null>(null);

  useEffect(() => {
    const loadStudentData = async () => {
      if (!classId || !studentId || !user) return;

      setLoading(true);
      try {
        // Load class info
        const { data: classData } = await apiService.getClassById(classId);
        if (classData) {
          setClassInfo({
            id: classData.id,
            name: classData.name,
            code: classData.code,
            term: classData.term,
          });
        }

        // Load student profile data
        const { data: profileData } = await apiService.getUserProfile(studentId);
        
        // IMPORTANT: Fetch student profile without classId to ensure profile consistency across classes
        // Student profiles (survey responses, background data) should be the same regardless of 
        // which class context they're viewed from
        const { data: studentProfileData } = await apiService.getStudentProfile(studentId);
        const { data: enrollmentData } = await apiService.getStudentEnrollments(studentId);
        
        // Try to load survey results
        const { data: surveyResults } = await apiService.getStudentSurveyResults(studentId);
        
        if (profileData) {
          // Convert database data to Student interface
          const realStudent: Student = {
            id: profileData.id,
            name: profileData.fullName || 'Unknown Student',
            email: profileData.email,
            enrollmentDate: enrollmentData?.[0]?.enrollment_date ? new Date(enrollmentData[0].enrollment_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            lastActive: studentProfileData?.updatedAt ? new Date(studentProfileData.updatedAt).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            engagementScore: calculateEngagementScore(studentProfileData),
            feedbackCompleted: 0,
            status: enrollmentData?.[0]?.status === 'active' ? 'active' : 'inactive',
            auraProfile: generateAuraFromProfile(studentProfileData),
            learningProfile: extractLearningProfile(studentProfileData)
          };

          setStudent(realStudent);
        }

        // Extract and set background data
        const extractedBackground = extractBackgroundFromProfile(studentProfileData);
        setBackgroundData(extractedBackground);

        // Set survey data if available
        if (surveyResults && surveyResults.hasResults && surveyResults.categories?.length > 0) {
          setSurveyData({
            scores: surveyResults.categories,
            backgroundData: extractedBackground || {},
            responses: [], // Survey responses aren't needed for display
            hasResults: true
          });
        }

      } catch (error) {
        console.error('Failed to load student data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStudentData();
  }, [classId, studentId, user]);

  // Helper functions for converting database data
  const calculateEngagementScore = (studentProfile: any) => {
    if (!studentProfile?.responses) return 0;
    
    const responses = studentProfile.responses;
    const totalCategories = Object.keys(responses).length;
    const completedCategories = Object.values(responses).filter((resp: any) => 
      Array.isArray(resp) && resp.length > 0
    ).length;
    
    return Math.round((completedCategories / totalCategories) * 100);
  };

  const generateAuraFromProfile = (studentProfile: any) => {
    if (!studentProfile?.responses) {
      return {
        dominant: 'Unknown Learner',
        secondary: 'Adaptive',
        color: '#6b7280',
        description: 'Learning profile not yet available. Complete the student questionnaire to generate an aura profile.'
      };
    }

    // Analyze responses to determine aura characteristics
    const responses = studentProfile.responses;
    
    // Simple heuristic based on participation comfort and learning environment
    const participationResponses = responses.participationcomfort || [];
    const learningEnvResponses = responses.learningenvironment || [];
    
    const isHighParticipation = participationResponses.includes('Strongly Agree');
    const isCollaborative = learningEnvResponses.includes('Strongly Agree');
    
    if (isHighParticipation && isCollaborative) {
      return {
        dominant: 'Social Learner',
        secondary: 'Collaborative',
        color: '#ef4444',
        description: 'Thrives in group settings and collaborative environments. Actively participates and engages with peers.'
      };
    } else if (isHighParticipation) {
      return {
        dominant: 'Confident Learner',
        secondary: 'Independent',
        color: '#10b981',
        description: 'Comfortable expressing ideas and participating actively. Shows confidence in learning environments.'
      };
    } else {
      return {
        dominant: 'Reflective Learner',
        secondary: 'Thoughtful',
        color: '#8b5cf6',
        description: 'Takes time to process information thoughtfully. Prefers careful consideration before participating.'
      };
    }
  };

  const extractLearningProfile = (studentProfile: any) => {
    if (!studentProfile?.responses) {
      return {
        academicBackground: ['Information not available'],
        learningGoals: ['Information not available'],
        preferredLearningStyles: ['Information not available'],
        participationComfort: ['Information not available'],
        workloadPreferences: ['Information not available'],
        assessmentStyles: ['Information not available']
      };
    }

    const responses = studentProfile.responses;
    
    return {
      academicBackground: responses.academicBackground || ['Information not provided'],
      learningGoals: responses.learningGoals || ['Information not provided'],
      preferredLearningStyles: responses.preferredLearningStyles || ['Information not provided'],
      participationComfort: responses.participationcomfort || ['Information not provided'],
      workloadPreferences: responses.workloadPreferences || ['Information not provided'],
      assessmentStyles: responses.assessmentStyles || ['Information not provided']
    };
  };


  const extractBackgroundFromProfile = (studentProfile: any): BackgroundResponse => {
    if (!studentProfile?.backgroundData && !studentProfile?.responses) {
      return {};
    }

    // Check backgroundData first (from ProfileUpdate), then fall back to responses (legacy)
    const data = studentProfile.backgroundData || studentProfile.responses || {};
    
    return {
      name: data.name || studentProfile.fullName || '',
      email: data.email || studentProfile.email || '',
      classLevel: data.classLevel || '',
      hometown: data.hometown || '',
      classCount: data.classCount || '',
      majorCount: data.majorCount || '',
      major: data.major || data.majors || '',
      firstLanguage: data.firstLanguage || '',
      dailyLanguage: data.dailyLanguage || '',
      englishProficiency: data.englishProficiency || 0,
      speakingComfort: data.speakingComfort || 0,
      listeningSkill: data.listeningSkill || 0,
      readingComfort: data.readingComfort || 0,
      writingAbility: data.writingAbility || 0,
      memorizing: data.courseworkEmphasis?.memorizing || data.memorizing || '',
      applying: data.courseworkEmphasis?.applying || data.applying || '',
      analyzing: data.courseworkEmphasis?.analyzing || data.analyzing || '',
      evaluating: data.courseworkEmphasis?.evaluating || data.evaluating || '',
      forming: data.courseworkEmphasis?.forming || data.forming || '',
      numericalAnalysis: data.numericalTasks?.numericalAnalysis || data.numericalAnalysis || '',
      numericalProblems: data.numericalTasks?.numericalProblems || data.numericalProblems || '',
      evaluatingNumerical: data.numericalTasks?.evaluatingNumerical || data.evaluatingNumerical || '',
      papers5Pages: data.writingTasks?.papers5Pages || data.papers5Pages || '',
      papers6to10Pages: data.writingTasks?.papers6to10Pages || data.papers6to10Pages || '',
      papers11Plus: data.writingTasks?.papers11Plus || data.papers11Plus || '',
      previousCourses: data.previousCourses || '',
      reasonsForTaking: data.reasonsForTaking || [],
      preferredCommunication: data.preferredCommunication || ''
    };
  };


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'at-risk': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!student || !classInfo) {
    return (
      <div>
        <ClassNavigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-gray-900">Student not found</h2>
            <p className="text-gray-600 mt-2">Unable to load student details.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <ClassNavigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                👤 {student.name}
              </h1>
              <p className="text-gray-600">{classInfo.name} • {classInfo.code}</p>
            </div>
            <div className="text-right">
              <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(student.status)}`}>
                {student.status}
              </span>
            </div>
          </div>
        </div>

        {/* Student Summary Card */}
        <div className="mb-8 p-6 bg-gradient-to-r from-emerald-50 to-lime-50 rounded-xl border border-emerald-100">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="flex items-center">
              <UserCircleIcon className="h-12 w-12 text-emerald-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-medium text-gray-900">{student.email}</p>
              </div>
            </div>
            <div className="flex items-center">
              <AcademicCapIcon className="h-12 w-12 text-blue-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Enrolled</p>
                <p className="font-medium text-gray-900">{student.enrollmentDate}</p>
              </div>
            </div>
            <div className="flex items-center">
              <ChartBarIcon className="h-12 w-12 text-purple-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Engagement</p>
                <p className="font-medium text-gray-900">{student.engagementScore}%</p>
              </div>
            </div>
            <div className="flex items-center">
              <ClockIcon className="h-12 w-12 text-orange-600 mr-3" />
              <div>
                <p className="text-sm text-gray-600">Last Active</p>
                <p className="font-medium text-gray-900">{student.lastActive}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Learning Profile */}
          <div className="space-y-8">
            {/* Aura Profile */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Learning Aura Profile</h3>
              <div className="flex items-start space-x-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl"
                  style={{ backgroundColor: student.auraProfile.color }}
                >
                  {student.auraProfile.dominant.charAt(0)}
                </div>
                <div className="flex-1">
                  <h4 className="text-xl font-semibold text-gray-900">{student.auraProfile.dominant}</h4>
                  <p className="text-sm text-gray-600 mb-2">Secondary: {student.auraProfile.secondary}</p>
                  <p className="text-gray-700">{student.auraProfile.description}</p>
                </div>
                {surveyData && surveyData.hasResults && (
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => navigate(`/dashboard/class/${classId}/student/${studentId}/survey`)}
                      className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                      View Survey Results
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Learning Profile Background */}
            {backgroundData && Object.keys(backgroundData).length > 0 ? (
              <div className="space-y-6">
                {/* Contact & Demographics */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Contact & Demographics</h4>
                    <div className="space-y-3">
                      {backgroundData.name && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="text-gray-900 font-medium">{backgroundData.name}</span>
                        </div>
                      )}
                      {backgroundData.email && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="text-gray-900">{backgroundData.email}</span>
                        </div>
                      )}
                      {backgroundData.classLevel && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Class Level:</span>
                          <span className="text-gray-900">{backgroundData.classLevel}</span>
                        </div>
                      )}
                      {backgroundData.hometown && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Hometown:</span>
                          <span className="text-gray-900">{backgroundData.hometown}</span>
                        </div>
                      )}
                      {backgroundData.major && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Major:</span>
                          <span className="text-gray-900">{backgroundData.major}</span>
                        </div>
                      )}
                      {backgroundData.classCount && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Classes This Term:</span>
                          <span className="text-gray-900">{backgroundData.classCount}</span>
                        </div>
                      )}
                      {backgroundData.preferredCommunication && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Preferred Communication:</span>
                          <span className="text-gray-900">{backgroundData.preferredCommunication}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Language Proficiency</h4>
                    <div className="space-y-3">
                      {backgroundData.firstLanguage && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">First Language:</span>
                          <span className="text-gray-900">{backgroundData.firstLanguage}</span>
                        </div>
                      )}
                      {backgroundData.dailyLanguage && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Daily Language:</span>
                          <span className="text-gray-900">{backgroundData.dailyLanguage}</span>
                        </div>
                      )}
                      {backgroundData.englishProficiency && backgroundData.englishProficiency > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">English Proficiency:</span>
                          <span className="text-gray-900">{backgroundData.englishProficiency}/5</span>
                        </div>
                      )}
                      {backgroundData.speakingComfort && backgroundData.speakingComfort > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Speaking Comfort:</span>
                          <span className="text-gray-900">{backgroundData.speakingComfort}/5</span>
                        </div>
                      )}
                      {backgroundData.readingComfort && backgroundData.readingComfort > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Reading Comfort:</span>
                          <span className="text-gray-900">{backgroundData.readingComfort}/5</span>
                        </div>
                      )}
                      {backgroundData.writingAbility && backgroundData.writingAbility > 0 && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Writing Ability:</span>
                          <span className="text-gray-900">{backgroundData.writingAbility}/5</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Academic Background */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Coursework Emphasis</h4>
                    <div className="space-y-3">
                      {backgroundData.memorizing && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Memorizing:</span>
                          <span className="text-gray-900">{backgroundData.memorizing}</span>
                        </div>
                      )}
                      {backgroundData.applying && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Applying:</span>
                          <span className="text-gray-900">{backgroundData.applying}</span>
                        </div>
                      )}
                      {backgroundData.analyzing && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Analyzing:</span>
                          <span className="text-gray-900">{backgroundData.analyzing}</span>
                        </div>
                      )}
                      {backgroundData.evaluating && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Evaluating:</span>
                          <span className="text-gray-900">{backgroundData.evaluating}</span>
                        </div>
                      )}
                      {backgroundData.forming && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Forming New Ideas:</span>
                          <span className="text-gray-900">{backgroundData.forming}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Course Background</h4>
                    <div className="space-y-3">
                      {backgroundData.previousCourses && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Previous Courses:</span>
                          <span className="text-gray-900">{backgroundData.previousCourses}</span>
                        </div>
                      )}
                      {backgroundData.reasonsForTaking && backgroundData.reasonsForTaking.length > 0 && (
                        <div>
                          <span className="text-gray-600 block mb-2">Reasons for Taking Class:</span>
                          <ul className="space-y-1">
                            {backgroundData.reasonsForTaking.map((reason, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-emerald-500 mr-2">•</span>
                                <span className="text-gray-700 text-sm">{reason}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Writing Experience */}
                {(backgroundData.papers5Pages || backgroundData.papers6to10Pages || backgroundData.papers11Plus) && (
                  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Writing Experience</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {backgroundData.papers5Pages && (
                        <div className="text-center">
                          <span className="text-2xl font-bold text-emerald-600">{backgroundData.papers5Pages}</span>
                          <p className="text-sm text-gray-600">Papers (1-5 pages)</p>
                        </div>
                      )}
                      {backgroundData.papers6to10Pages && (
                        <div className="text-center">
                          <span className="text-2xl font-bold text-blue-600">{backgroundData.papers6to10Pages}</span>
                          <p className="text-sm text-gray-600">Papers (6-10 pages)</p>
                        </div>
                      )}
                      {backgroundData.papers11Plus && (
                        <div className="text-center">
                          <span className="text-2xl font-bold text-purple-600">{backgroundData.papers11Plus}</span>
                          <p className="text-sm text-gray-600">Papers (11+ pages)</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="text-gray-400 text-6xl mb-4">📝</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">Learning Profile Not Available</h4>
                <p className="text-gray-600">
                  The student hasn't completed their background profile yet. 
                  Encourage them to update their profile information.
                </p>
              </div>
            )}
          </div>

      </div>
    </div>
  );
};

export default StudentDetails;